import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import { useGetClassrooms } from "../../query/childQuery";
import { useGetFeePolicy } from "../../query/settingsQuery";
import { usePreviewMonth, useSendMonth } from "../../query/feesQuery";
import { MONTH_NAMES, formatAmount } from "./feeHelpers";
import "../teachers/addTeacher.css";
import "./fees.css";

function dueFor(policy, month, year) {
  const day = Math.min(28, Math.max(1, Number(policy?.dueDayOfMonth) || 10));
  const candidate = dayjs(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
  if (candidate.isValid() && candidate.isAfter(dayjs(), "day")) return candidate.format("YYYY-MM-DD");
  return dayjs().add(1, "day").format("YYYY-MM-DD");
}

export default function SendFees() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { data: classrooms = [] } = useGetClassrooms();
  const { data: policy } = useGetFeePolicy();
  const { mutate: preview, isPending: previewing } = usePreviewMonth();
  const { mutate: send, isPending: sending } = useSendMonth();
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(currentYear));
  const [classroom, setClassroom] = useState("");
  const [issuedAt, setIssuedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [dueDate, setDueDate] = useState("");
  const [result, setResult] = useState(null);

  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  useEffect(() => {
    setDueDate(dueFor(policy, Number(month), Number(year)));
  }, [policy, month, year]);

  const payload = {
    month: Number(month),
    year: Number(year),
    classroom: classroom || undefined,
    issuedAt,
    dueDate,
  };

  const runPreview = () => {
    preview(payload, {
      onSuccess: (data) => setResult(data),
      onError: (error) => swal("Error", error?.message || "Could not preview vouchers", "error"),
    });
  };

  const runSend = () => {
    const count = result?.included?.length || 0;
    if (!count) {
      swal("Nothing to send", "Preview the month first. Students without a fee, or with a voucher already sent, are skipped.", "info");
      return;
    }
    swal({
      title: "Send vouchers?",
      text: `${count} ${MONTH_NAMES[Number(month) - 1]} ${year} voucher${count === 1 ? "" : "s"} will be sent. Parents get a notice.`,
      icon: "warning",
      buttons: ["Not now", "Send"],
    }).then((confirmed) => {
      if (!confirmed) return;
      send(payload, {
        onSuccess: (data) => {
          setResult((current) => ({
            included: [],
            skipped: data?.skipped || current?.skipped || [],
          }));
          swal("Sent", `${data?.sent || 0} voucher${data?.sent === 1 ? "" : "s"} sent`, "success");
        },
        onError: (error) => swal("Error", error?.message || "Could not send vouchers", "error"),
      });
    });
  };

  return (
    <div className="tf-page">
      <section className="tf-card">
        <h2 className="tf-card-head">Send monthly vouchers</h2>
        <div className="tf-card-body">
          <p className="fee-send-lead">
            One voucher is sent per student for the month. Students who already have one, or who have no active monthly fee, are skipped.
          </p>
          <div className="fee-send-grid">
            <label>
              Month
              <select value={month} onChange={(event) => { setMonth(event.target.value); setResult(null); }}>
                {MONTH_NAMES.map((label, index) => (
                  <option key={label} value={String(index + 1)}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              Year
              <select value={year} onChange={(event) => { setYear(event.target.value); setResult(null); }}>
                {years.map((value) => (
                  <option key={value} value={String(value)}>{value}</option>
                ))}
              </select>
            </label>
            <label>
              Class
              <select value={classroom} onChange={(event) => { setClassroom(event.target.value); setResult(null); }}>
                <option value="">All classes</option>
                {classrooms.map((room) => (
                  <option key={room._id} value={room._id}>{room.classroomName}</option>
                ))}
              </select>
            </label>
            <label>
              Send date
              <input type="date" value={issuedAt} onChange={(event) => setIssuedAt(event.target.value)} />
            </label>
            <label>
              Due date
              <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
            </label>
          </div>
        </div>
      </section>

      {result ? (
        <section className="tf-card">
          <h2 className="tf-card-head">
            {result.included?.length || 0} ready · {result.skipped?.length || 0} skipped
          </h2>
          <div className="tf-card-body">
            <div className="fee-send-columns">
              <div>
                <h3>Will be sent</h3>
                {result.included?.length ? (
                  <ul className="fee-send-list">
                    {result.included.map((row) => (
                      <li key={row._id}>
                        <span>{row.name}</span>
                        <strong>PKR {formatAmount(row.netAmount)}</strong>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="fee-send-empty">No students are ready for this month.</p>
                )}
              </div>
              <div>
                <h3>Skipped</h3>
                {result.skipped?.length ? (
                  <ul className="fee-send-list">
                    {result.skipped.map((row) => (
                      <li key={`${row._id}-${row.reason}`}>
                        <span>{row.name}</span>
                        <em>{row.reason}</em>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="fee-send-empty">Nothing was skipped.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <div className="tf-actions">
        <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/fees")}>
          Back
        </button>
        <button type="button" className="tf-btn is-ghost" onClick={runPreview} disabled={previewing || sending}>
          {previewing ? "Checking…" : "Preview"}
        </button>
        <button type="button" className="tf-btn is-primary" onClick={runSend} disabled={sending || previewing}>
          {sending ? "Sending…" : "Send vouchers"}
        </button>
      </div>
    </div>
  );
}
